/**
 * AELO pre-launch homepage interactions.
 * Lightweight visual polish; Shopify customer form handles submission.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-aelo-prelaunch-root]');
  if (!root) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if ('IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('aelo-prelaunch-reveal');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -32px 0px' });

    root.querySelectorAll([
      '.aelo-prelaunch-section-heading',
      '.aelo-prelaunch-problem-card',
      '.aelo-prelaunch-card',
      '.aelo-prelaunch-materials-stage',
      '.aelo-prelaunch-materials-point',
      '.aelo-prelaunch-step',
      '.aelo-prelaunch-faq-item',
      '.aelo-prelaunch-list-visual',
      '.aelo-prelaunch-form-panel'
    ].join(',')).forEach((el) => {
      revealObserver.observe(el);
    });
  }

  if (prefersReducedMotion) return;

  const canvas = root.querySelector('.aelo-prelaunch-canvas');
  const stage = canvas ? canvas.parentElement : null;
  if (!canvas || !stage) return;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  let animationId = null;
  let mouseX = 0.55;
  let mouseY = 0.45;
  let targetMouseX = 0.55;
  let targetMouseY = 0.45;
  let sweep = 0;
  let particles = [];

  function resize() {
    const rect = stage.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function createParticles() {
    const rect = stage.getBoundingClientRect();
    const count = window.matchMedia('(max-width: 639px)').matches ? 10 : 22;
    particles = Array.from({ length: count }, () => ({
      x: Math.random() * rect.width,
      y: Math.random() * rect.height,
      r: 1.2 + Math.random() * 2.8,
      vx: -0.1 + Math.random() * 0.2,
      vy: -0.06 + Math.random() * 0.12,
      opacity: 0.1 + Math.random() * 0.14,
      phase: Math.random() * Math.PI * 2,
    }));
  }

  function drawLens(x, y, radius) {
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, 'rgba(255,255,255,0.28)');
    gradient.addColorStop(0.48, 'rgba(147,183,213,0.1)');
    gradient.addColorStop(1, 'rgba(147,183,213,0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = 'rgba(147,183,213,0.12)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.72, 0, Math.PI * 2);
    ctx.stroke();
  }

  function tick() {
    const rect = stage.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);

    if (window.matchMedia('(max-width: 639px)').matches) {
      sweep += 0.008;
      targetMouseX = 0.5 + Math.sin(sweep) * 0.28;
      targetMouseY = 0.5 + Math.cos(sweep * 0.7) * 0.22;
    }

    mouseX += (targetMouseX - mouseX) * 0.06;
    mouseY += (targetMouseY - mouseY) * 0.06;

    const lensX = mouseX * rect.width;
    const lensY = mouseY * rect.height;
    const lensRadius = Math.min(rect.width, rect.height) * 0.43;
    drawLens(lensX, lensY, lensRadius);

    particles.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy + Math.sin(Date.now() * 0.001 + p.phase) * 0.03;
      if (p.x < -8) p.x = rect.width + 8;
      if (p.x > rect.width + 8) p.x = -8;
      if (p.y < -8) p.y = rect.height + 8;
      if (p.y > rect.height + 8) p.y = -8;

      const dx = p.x - lensX;
      const dy = p.y - lensY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const opacity = dist < lensRadius ? Math.min(p.opacity * 1.5, 0.28) : p.opacity;
      ctx.fillStyle = 'rgba(147,183,213,' + opacity + ')';
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    });

    animationId = requestAnimationFrame(tick);
  }

  stage.addEventListener('mousemove', (event) => {
    const rect = stage.getBoundingClientRect();
    targetMouseX = (event.clientX - rect.left) / rect.width;
    targetMouseY = (event.clientY - rect.top) / rect.height;
  }, { passive: true });

  stage.addEventListener('mouseleave', () => {
    targetMouseX = 0.55;
    targetMouseY = 0.45;
  }, { passive: true });

  resize();
  createParticles();
  tick();

  window.addEventListener('resize', () => {
    resize();
    createParticles();
  }, { passive: true });

  window.addEventListener('pagehide', () => {
    if (animationId) cancelAnimationFrame(animationId);
  }, { once: true });
})();
